import { qrmInstance } from './index'
import * as Sentry from '@sentry/react'
import { CustomError } from 'featureSliced/features/Error'
// import { Translations } from 'featureSliced/shared/store/reducers/TranslationsReducer/types'
import { IRestaurant } from 'featureSliced/shared/store/reducers/RestaurantsReducer/types'
import {
  ICategory,
  IProductInfo,
} from 'featureSliced/shared/store/reducers/CategoriesReducer/types'
import {
  IAllergen,
  ITable,
} from 'featureSliced/shared/store/reducers/CommonReducer/types'
import { handleLocalStorage } from 'featureSliced/shared/utils/handleLocalStorage'
import {
  CloudOrdersGetPathes,
  QRMgetPathes,
  WalletPathes,
} from '../ApiGETPathes'
import { AdyenPaymentMethodsType, CloudOrderType, IHistoryItem } from './types'
import {
  IAppResponse,
  ICompanyData,
} from 'featureSliced/shared/store/reducers/AppReducer/types'

const withToken = true
const withoutToken = false
const cacheFirst = true

export class qrMenuGET {
  private static withToken = true

  public static async companyData(payload?: {
    guid?: string
    branchId?: number
  }): Promise<ICompanyData> {
    const { guid, dataEnc } = handleLocalStorage(['guid', 'dataEnc'])
    const company = payload?.guid || dataEnc || guid
    if (!company) {
      throw new CustomError('Missing company identifier', 400)
    }
    const res: ICompanyData = await qrmInstance.get(
      `${QRMgetPathes.GET_COMPANIES}/${company}`,
      withToken,
      { branch: payload?.branchId },
      // `${payload?.branchId ? `?branch=${payload.branchId}` : ''}`,
      cacheFirst
    )
    return res
  }

  public static async companies(): Promise<any[]> {
    const res: any[] = await qrmInstance.get(
      `${QRMgetPathes.GET_COMPANIES}`,
      withoutToken,
      {},
      cacheFirst
    )
    return res
  }

  public static async setup(): Promise<IAppResponse> {
    const { guid, dataEnc } = handleLocalStorage(['guid', 'dataEnc'])
    const company = dataEnc || guid

    Sentry.setTags({
      dataEnc,
    })

    if (!company) {
      throw new CustomError('Missing company identifier', 400)
    }

    const res: IAppResponse = await qrmInstance.get(
      `${QRMgetPathes.GET_COMPANIES}/${company}${QRMgetPathes.GET_SETUP}`,
      withoutToken,
      {
        app_name:
          process.env.REACT_APP_IS_CLIC === '0' ? 'qrmenu' : 'clickcollect',
      },
      cacheFirst
    )
    return res
  }

  public static async categories(): Promise<ICategory[]> {
    const res: ICategory[] = await qrmInstance.get(QRMgetPathes.GET_CATEGORIES)
    return res
  }

  public static async products(search = ''): Promise<ICategory[]> {
    const res: ICategory[] = await qrmInstance.get(
      QRMgetPathes.GET_PRODUCTS,
      withoutToken,
      { search }
      // `search=${search}`
    )
    return res
  }

  public static async products_v2(search = ''): Promise<ICategory[]> {
    const params: Record<string, string> = { search }
    // C&C browses for a future pickup slot: pass the chosen time so the backend
    // evaluates Orders availability windows (type=1) against the PICKUP date instead
    // of "now" (CW-2694). QR menu (dine-in) sends nothing → Sale windows @ now.
    if (process.env.REACT_APP_IS_CLIC === '1') {
      const { pickUpTime } = handleLocalStorage(['pickUpTime']) as {
        pickUpTime: string | null
      }
      if (pickUpTime) params.pickupTime = pickUpTime
    }
    const res: ICategory[] = await qrmInstance.get(
      QRMgetPathes.GET_PRODUCTS_V2,
      withoutToken,
      params
    )
    return res
    // return res?.sort((a, b) => (a?.pageIndex > b?.pageIndex ? 1 : -1))
  }

  // QRM-640: declaration + nutrient table for the product-info sheet, fetched
  // lazily on "i" tap (the catalogue only carries hasProductInfo). Never
  // cache-first: a stale declaration is worse than a spinner.
  public static async productInfo(
    categoryId: number,
    productId: number
  ): Promise<IProductInfo> {
    const res: IProductInfo = await qrmInstance.get(
      QRMgetPathes.GET_PRODUCT_INFO,
      withoutToken,
      { categoryId, productId }
    )
    return res
  }

  public static async allergens(): Promise<IAllergen[]> {
    const res: IAllergen[] = await qrmInstance.get(QRMgetPathes.GET_ALLERGENS)
    return res
  }

  public static async tableInfo(
    caption?: string | number,
    table?: string | number
  ) {
    const res: ITable[] = await qrmInstance.get(
      QRMgetPathes.GET_TABLE_INFO,
      withoutToken,
      { table, caption },
      // [
      //   `${table ? `table=${table}` : ''}`,
      //   `${caption ? `caption=${caption}` : ''}`,
      // ]?.join('&'),
      cacheFirst
    )
    return res
  }

  public static async restaurants(
    ActiveBranchID?: string,
    guidParam?: string | null
  ): Promise<IRestaurant[]> {
    // An explicit guid replaces the localStorage trio entirely
    // (ignoreQueryParams) — so it must be sent as the company param itself,
    // otherwise the request goes out with no company at all and 400s.
    const res: IRestaurant[] = await qrmInstance.get(
      QRMgetPathes.GET_RESTAURANTS,
      withoutToken,
      {
        branch: ActiveBranchID,
        cc_active: process.env.REACT_APP_IS_CLIC,
        ...(guidParam ? { company: guidParam } : {}),
      },
      cacheFirst,
      false,
      !!guidParam
    )
    return res
  }

  // public static async paymentDetails(
  //   table: number
  // ): Promise<PaymentDetailsType> {
  //   const res: PaymentDetailsType = await qrmInstance.get(
  //     QRMgetPathes.GET_PAYMENT_DETAILS,
  //     withoutToken,
  //     { table }
  //     // `table=${table}`
  //   )
  //   return res
  // }

  public static async ordersHistory(): Promise<IHistoryItem[]> {
    const res: IHistoryItem[] = await qrmInstance.get(
      QRMgetPathes.GET_ORDERS,
      withToken,
      {
        app_name:
          process.env.REACT_APP_IS_CLIC === '0' ? 'qrmenu' : 'clickcollect',
      }
      // `app_name=${
      //   process.env.REACT_APP_IS_CLIC === '0' ? 'qrmenu' : 'clickcollect'
      // }`
    )
    return res
  }

  public static async ordersHistoryWallet(
    walletCardId: string
  ): Promise<IHistoryItem[]> {
    const res: IHistoryItem[] = await qrmInstance.get(
      WalletPathes.GET_ORDERS,
      withoutToken,
      { cardId: walletCardId }
      // `cardId=${walletCardId}`
    )
    return res
  }

  public static async getSpecials(): Promise<any[]> {
    const res: any[] = await qrmInstance.get(QRMgetPathes.GET_SPECIALS)
    return res
  }

  public static async getKitchenTimeSlots() {
    const res: any = await qrmInstance.get(QRMgetPathes.GET_KITCHEN_TIME_SLOTS)
    return res
  }

  public static async getSoldOutItems() {
    const res: { articleId: number }[] = await qrmInstance.get(
      QRMgetPathes.GET_SOLD_OUT_ITEMS
    )
    return res
  }

  // deprecated
  public static async translations(appName = 'clickCollect'): Promise<any> {
    const { guid, dataEnc } = handleLocalStorage(['guid', 'dataEnc'])
    const searchParams = new URLSearchParams(window.location.search)

    const guidSearch = searchParams.get('guid')
    const dataSearch = searchParams.get('data')

    const queryParams = {
      app: appName,
      data: dataSearch ? dataSearch : dataEnc ? dataEnc : '',
      company:
        !dataSearch && !dataEnc && guidSearch ? guidSearch : guid ? guid : '',
    }
    // .filter(Boolean)
    // .join('&')

    const res = await qrmInstance.get(
      QRMgetPathes.GET_TRANSLATIONS,
      withoutToken,
      queryParams,
      cacheFirst,
      false,
      true
    )
    return res
  }

  public static async notifications() {
    // TODO: add type
    const result: any = await qrmInstance.get(
      QRMgetPathes.NOTIFICATIONS,
      withToken,
      {
        app_name:
          process.env.REACT_APP_IS_CLIC === '0' ? 'qrmenu' : 'clickcollect',
      }
      // `app_name=${
      //   process.env.REACT_APP_IS_CLIC === '0' ? 'qrmenu' : 'clickcollect'
      // }`
    )
    return result
  }

  public static async notificationsAmount() {
    // TODO: add type
    const result: any = await qrmInstance.get(
      QRMgetPathes.NOTIFICATIONS_AMOUNT,
      withToken,
      {
        app_name:
          process.env.REACT_APP_IS_CLIC === '0' ? 'qrmenu' : 'clickcollect',
      }
      // `app_name=${
      //   process.env.REACT_APP_IS_CLIC === '0' ? 'qrmenu' : 'clickcollect'
      // }`
    )
    return result?.unreadCount || 0
  }

  public static async cloudOrders(table?: string | number | null) {
    const res: CloudOrderType = await qrmInstance.get(
      CloudOrdersGetPathes.GET_ORDERS,
      withoutToken,
      { table }
      // `${table ? `&table=${table}` : ''}`
    )
    return res
  }

  public static async modVer(modVer: number, orderGuid?: string) {
    const res: CloudOrderType = await qrmInstance.get(
      CloudOrdersGetPathes.GET_MOD_VER,
      withoutToken,
      {
        orderGuid,
        modVer,
      }
      // [
      //   `${orderGuid ? `&orderGuid=${orderGuid}` : ''}`,
      //   `${modVer ? `&modVer=${modVer}` : ''}`,
      // ]?.join('&')
    )
    return res
  }

  // ADYEN PAYMENT

  public static async adyenPaymentMethods(shopId: string) {
    const res: AdyenPaymentMethodsType = await qrmInstance.get(
      QRMgetPathes.ADYEN_PAYMENT_METHODS,
      withToken,
      { shopId }
    )
    return res
  }

  public static async createAdyenPayment(payload: any) {
    const res: any = await qrmInstance.get(
      QRMgetPathes.CREATE_ADYEN,
      withoutToken,
      payload
    )
    return res
  }
}
